import React from 'react'
import { useTranslation } from 'react-i18next'

import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import PropTypes from 'prop-types'

import { CARE_CREDIT_URL } from '../constants'
import { useTheme } from '../theme'
import { useLocation } from '../useLocation'
import CareCreditCardArt from './CareCreditCardArt.png'
import { buildCareCreditUrl, MEDIUM_TYPES } from './careCreditUrlBuilder'

export const CareCreditPromo = ({
  store,
  bookingType,
  appointmentId,
  medium = MEDIUM_TYPES.APP,
}) => {
  const { showCareCreditPromo } = useLocation(store)
  const { t } = useTranslation()
  const theme = useTheme()

  if (!showCareCreditPromo) return null

  // Build dynamic URL if booking context is provided
  const careCreditUrl =
    bookingType && appointmentId
      ? buildCareCreditUrl({
          baseUrl: CARE_CREDIT_URL,
          bookingType,
          appointmentId,
          medium,
        })
      : CARE_CREDIT_URL
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '12px',
        borderColor: theme.palette.secondary.main,
        margin: '120px auto',
        position: 'relative',
        maxWidth: '400px',
        overflow: 'initial',
      }}
    >
      <CardMedia
        image={CareCreditCardArt}
        sx={{
          width: '30%',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: '-36px',
          height: 'auto',
          aspectRatio: '480/308',
          backgroundColor: '#fff',
          position: 'relative',
          objectFit: 'contain',
          '@media (min-width: 300px)': {
            marginTop: '-40px',
          },
          '@media (min-width: 600px)': {
            marginTop: '-50px',
          },
        }}
      />
      <CardContent
        sx={{
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          paddingTop: '24px',
        }}
      >
        <Typography variant="h6">{t('Looking to pay over time?')}</Typography>
        <Typography variant="body1" sx={{ color: theme.palette.neutral[950] }}>
          {t(
            'CareCredit is a health and wellness credit card with flexible financing options so you can pay over time.*'
          )}
        </Typography>
        <Button
          data-testid="care-credit-promo-button"
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          href={careCreditUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ borderRadius: '24px' }}
        >
          {t('Get Started')}
        </Button>
        <List dense={true} mb={2}>
          <ListItem sx={{ alignItems: 'flex-start' }}>
            <ListItemIcon fontSize="small" sx={{ minWidth: '40px' }}>
              <CheckRoundedIcon />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                sx: { color: theme.palette.neutral[950] },
              }}
            >
              {t('Accepted at 285k+ locations')}
            </ListItemText>
          </ListItem>
          <ListItem sx={{ alignItems: 'flex-start' }}>
            <ListItemIcon fontSize="small" sx={{ minWidth: '40px' }}>
              <CheckRoundedIcon />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                sx: { color: theme.palette.neutral[950] },
              }}
            >
              {t('No credit score impact to see if you qualify')}
            </ListItemText>
          </ListItem>
        </List>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.neutral[950], fontWeight: 400 }}
        >
          {t('*Subject to credit approval')}
        </Typography>
      </CardContent>
    </Card>
  )
}

CareCreditPromo.propTypes = {
  store: PropTypes.func.isRequired,
  bookingType: PropTypes.string,
  appointmentId: PropTypes.string,
  medium: PropTypes.string,
}
